import { EditorQueries, EditorState, QueryMethods, SerializedNode } from '@candulabs/craft';
import flatten from 'lodash/flatten';
import { Text } from 'slate';

import { SlateElement } from 'craft/rte/types';
import { CraftNode } from 'craft/types';

// Convert a Craft node to Slate node
export const craftNodeToSlateNode = (query: any, nodeId: string) => {
  const craftNode = query.node(nodeId).get();

  const { id, data } = craftNode;
  const { name, type, nodes: childNodes } = data;

  const children = childNodes?.map((childId: string) => craftNodeToSlateNode(query, childId)) || [];

  const toSlateConverter = (type as any).slate && (type as any).slate.toSlateNode;

  const slateNode = {
    id,
    type: name,
    ...(children.length > 0 ? { children } : {}),
  };

  if (toSlateConverter) {
    toSlateConverter(craftNode)(slateNode);
  }

  // Just to maintain some symmetry with Slate's API where the leaf element typically does not have a "type" prop
  if (Text.isText(slateNode)) {
    delete slateNode.type;
  }

  return slateNode;
};

export const slateNodesToCraft = (
  state: EditorState,
  slateNodes: SlateElement[],
  parentId: string,
): any => {
  const query = QueryMethods(state);
  const resolvers = query.getOptions().resolver;

  return flatten(
    slateNodes.map((slateNode) => {
      const existingCraftNode = state.nodes[slateNode.id];

      const type = Text.isText(slateNode) ? resolvers.Text : resolvers[slateNode.type];
      const toCraftConverter = type.slate?.toCraftNode;

      const newCraftNode = query
        .parseFreshNode({
          id: slateNode.id,
          data: {
            type,
            parent: parentId,
            nodes: slateNode.children?.map((childNode: SlateElement) => childNode.id) || [],
          },
        })
        .toNode((node) => {
          if (toCraftConverter) {
            toCraftConverter(slateNode)(node);
          }
        });

      if (existingCraftNode?.data.type === newCraftNode.data.type) {
        // eslint-disable-next-line no-param-reassign
        state.nodes[slateNode.id].data = newCraftNode.data;
      } else {
        // eslint-disable-next-line no-param-reassign
        state.nodes[slateNode.id] = newCraftNode;
      }

      return [
        newCraftNode,
        ...(slateNode.children
          ? slateNodesToCraft(state, slateNode.children as SlateElement[], slateNode.id)
          : []),
      ];
    }),
  );
};

export const slateToCraftNodes = (
  query: EditorQueries,
  slateNodes: SlateElement[],
  parentId: string,
): any => {
  const resolvers = query.getOptions().resolver;
  return flatten(
    slateNodes.map((slateNode) => {
      const type = Text.isText(slateNode) ? resolvers.Text : resolvers[slateNode.type];
      const toCraftConverter = type.slate?.toCraftNode;

      const newCraftNode = query
        .parseFreshNode({
          id: slateNode.id,
          data: {
            type: slateNode.type || 'Text',
            parent: parentId,
            nodes: slateNode.children?.map((childNode: SlateElement) => childNode.id) || [],
          },
        })
        .toNode((node) => {
          if (toCraftConverter) {
            toCraftConverter(slateNode)(node);
          }
        });

      return [
        newCraftNode,
        ...(slateNode.children
          ? slateToCraftNodes(query, slateNode.children as SlateElement[], slateNode.id)
          : []),
      ];
    }),
  );
};

export type Tree = {
  nodes: Record<string, SerializedNode>;
  rootNodeId: string;
};

export const createTreeFromCraftNodes = (craftNodes: CraftNode[], parentNodeId: string): Tree[] => {
  const treeTemplateArray = craftNodes.reduce((acc: Tree[], node: CraftNode) => {
    return node.data.parent === parentNodeId ? [...acc, { rootNodeId: node.id, nodes: {} }] : acc;
  }, []);
  return craftNodes.reduce((acc: Tree[], node: CraftNode) => {
    const serializedNode: SerializedNode = {
      ...node.data,
      type: { resolvedName: node.data.name },
    };
    const positionInTree = acc.findIndex(
      (item) =>
        item.rootNodeId === node.data.parent || // is directly related to rootNode
        item.rootNodeId === node.id || // is directly related to rootNode
        Object.keys(item.nodes).includes(node.data.parent), // is a descendant from a related node
    );
    acc[positionInTree].nodes[node.id] = serializedNode;
    return acc;
  }, treeTemplateArray);
};
